(function () {
  let intersectionObserver = null;
  let resizeObserver = null;
  let lastRatio = 1;
  let autoplayTimer = null;
  let exitTimeout = null;
  let messages = [];
  let activeIndex = 0;
  let autoplayDuration = 5000;
  // Pause flags: hover (storefront) and editor block-select must not clobber each other
  let hoverPaused = false;
  let editorPaused = false;
  let hoverTarget = null;
  let focusPaused = false;
  let focusTarget = null;

  const ACTIVE_CLASS = "announcement-bar-message--active";
  const EXIT_CLASS = "announcement-bar-message--exit";

  const updateHeight = (intersectionRatio = 1) => {
    const annBar = document.querySelector(".announcement-section");
    const annBarHeight = annBar?.getBoundingClientRect().height || 0;
    const effectiveHeight = (annBarHeight * intersectionRatio).toFixed(2);
    document.documentElement.style.setProperty(
      "--ann-height",
      `${effectiveHeight}px`
    );
  };

  const stopAutoplay = () => {
    if (autoplayTimer) {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const teardown = () => {
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    stopAutoplay();
    if (exitTimeout) {
      clearTimeout(exitTimeout);
      exitTimeout = null;
    }
    if (hoverTarget) {
      hoverTarget.removeEventListener("mouseenter", handleBarMouseEnter);
      hoverTarget.removeEventListener("mouseleave", handleBarMouseLeave);
      hoverTarget = null;
    }
    if (focusTarget) {
      focusTarget.removeEventListener("focusin", handleBarFocusIn);
      focusTarget.removeEventListener("focusout", handleBarFocusOut);
      focusTarget = null;
    }
    hoverPaused = false;
    focusPaused = false;
    editorPaused = false;
    messages = [];
  };

  // Inactive messages stack in the same grid cell — drop their links from the tab order / a11y tree
  const syncMessageA11y = () => {
    messages.forEach((message, index) => {
      const isActive = index === activeIndex;
      if (isActive) {
        message.removeAttribute("aria-hidden");
      } else {
        message.setAttribute("aria-hidden", "true");
      }
      message.querySelectorAll("a").forEach((link) => {
        if (isActive) {
          link.removeAttribute("tabindex");
        } else {
          link.setAttribute("tabindex", "-1");
        }
      });
    });
  };

  const setActiveMessage = (index) => {
    const next = messages[index];
    if (!next) return;

    const current = messages[activeIndex];
    if (exitTimeout) {
      clearTimeout(exitTimeout);
      exitTimeout = null;
    }
    messages.forEach((message) => message.classList.remove(EXIT_CLASS));

    if (current && current !== next) {
      current.classList.remove(ACTIVE_CLASS);
      current.classList.add(EXIT_CLASS);
      exitTimeout = setTimeout(() => {
        current.classList.remove(EXIT_CLASS);
        exitTimeout = null;
      }, 400);
    }

    next.classList.add(ACTIVE_CLASS);
    activeIndex = index;
    syncMessageA11y();
  };

  const startAutoplay = () => {
    stopAutoplay();
    if (messages.length < 2) return;

    autoplayTimer = setInterval(() => {
      setActiveMessage((activeIndex + 1) % messages.length);
    }, autoplayDuration);
  };

  // Restart the cycle only when no pause source is active
  const resumeAutoplay = () => {
    if (hoverPaused || focusPaused || editorPaused) return;
    startAutoplay();
  };

  const handleBarMouseEnter = () => {
    hoverPaused = true;
    stopAutoplay();
  };

  const handleBarMouseLeave = () => {
    hoverPaused = false;
    resumeAutoplay();
  };

  // A rotation would hide the focused message link and drop focus to <body>, so
  // keyboard focus always holds it, whatever the hover setting says.
  const handleBarFocusIn = () => {
    focusPaused = true;
    stopAutoplay();
  };

  const handleBarFocusOut = (event) => {
    if (focusTarget?.contains(event.relatedTarget)) return;
    focusPaused = false;
    resumeAutoplay();
  };

  const initMessages = () => {
    const bar = document.querySelector(
      ".announcement-section .announcement-bar"
    );
    if (!bar) return;

    const wrapper = bar.querySelector(".announcement-bar__messages");
    if (!wrapper) return;

    autoplayDuration = parseInt(bar.dataset.autoplayDuration, 10) || 5000;
    messages = Array.from(
      wrapper.querySelectorAll(".announcement-bar-message")
    );
    activeIndex = 0;
    messages.forEach((message, index) => {
      message.classList.remove(EXIT_CLASS);
      message.classList.toggle(ACTIVE_CLASS, index === 0);
    });
    syncMessageA11y();
    wrapper.classList.add("announcement-bar__messages--ready");
    resumeAutoplay();
  };

  const initAnnBar = () => {
    const annBar = document.querySelector(".announcement-section");
    if (!annBar) return;

    const bar = annBar.querySelector(".announcement-bar");
    if (!bar) return;
    const pauseOnHover = bar.dataset.pauseOnHover === "true";

    teardown();

    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            updateHeight(entry.intersectionRatio);
            lastRatio = entry.intersectionRatio;
          } else {
            document.documentElement.style.setProperty("--ann-height", "0px");
            lastRatio = 0;
          }
        });
      },
      {
        threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
      }
    );

    resizeObserver = new ResizeObserver(() => {
      updateHeight(lastRatio);
    });

    intersectionObserver.observe(annBar);
    resizeObserver.observe(annBar);

    focusTarget = annBar;
    focusTarget.addEventListener("focusin", handleBarFocusIn);
    focusTarget.addEventListener("focusout", handleBarFocusOut);

    // Hover-capable devices only: touch taps must not freeze the rotation
    if (pauseOnHover && window.matchMedia("(hover: hover)").matches) {
      hoverTarget = annBar;
      hoverTarget.addEventListener("mouseenter", handleBarMouseEnter);
      hoverTarget.addEventListener("mouseleave", handleBarMouseLeave);
    }

    initMessages();
  };

  initAnnBar();

  if (!window.__themeAnnBarEvents) {
    window.__themeAnnBarEvents = true;

    document.addEventListener("shopify:section:load", initAnnBar);
    document.addEventListener("shopify:section:reorder", initAnnBar);
    document.addEventListener("shopify:section:unload", (event) => {
      const target = event.target;
      const isAnnBar =
        target.classList?.contains("announcement-section") ||
        !!target.querySelector?.(".announcement-section");
      if (isAnnBar) teardown();
    });

    // Theme editor: show the selected message and pause autoplay while it is selected
    document.addEventListener("shopify:block:select", (event) => {
      const message = event.target?.closest?.(".announcement-bar-message");
      if (!message) return;
      const index = messages.indexOf(message);
      if (index === -1) return;
      editorPaused = true;
      stopAutoplay();
      setActiveMessage(index);
    });

    document.addEventListener("shopify:block:deselect", (event) => {
      const message = event.target?.closest?.(".announcement-bar-message");
      if (!message) return;
      editorPaused = false;
      resumeAutoplay();
    });
  }
})();
